import { useUpdateContact } from '@eigtech/ui-shared-contacts'
import {
  Center,
  CircularProgress,
  ComposedAlert,
  ComposedDrawerWrapperProps,
  useToast,
} from '@eigtech/ui-shared-dave'
import { FormDrawer, createForm } from '@eigtech/ui-shared-forms'
import { z } from 'zod'
import { useGetDelegateUserActiveContacts } from '../../../api'

export type EstimatorLicenseDrawerProps = ComposedDrawerWrapperProps

export function EstimatorLicenseDrawer(props: EstimatorLicenseDrawerProps) {
  const { isContactsError, isContactsPending, contacts } = useGetDelegateUserActiveContacts()

  const contact = contacts.length === 1 ? contacts[0] : undefined

  const form = useForm({
    defaultValues: {
      licenseNumber: contact?.metadata.licenseNumber ?? undefined,
    },
  })

  const { mutateAsync: updateContact, isError: isUpdateError } = useUpdateContact()

  const toast = useToast()

  async function onSubmit({ licenseNumber }: EstimatorLicenseForm) {
    if (!contact) return

    await updateContact({
      ...contact,
      metadata: {
        ...contact.metadata,
        licenseNumber,
      },
    })

    toast({
      status: 'success',
      title: 'Successfully updated your license number!',
    })

    props.onClose()
  }

  return (
    <FormDrawer {...props} form={form} title="Update Your License" onSubmit={onSubmit}>
      {isContactsPending ? (
        <Center>
          <CircularProgress isIndeterminate size={100} />
        </Center>
      ) : isContactsError ? (
        <ComposedAlert
          alert={{
            title: 'Could not load your contact information',
            description: 'Please try again. If the issue persists, contact EIG Tech Support.',
          }}
          status="error"
        />
      ) : contacts.length > 1 ? (
        <ComposedAlert
          alert={{
            title: 'Your contact information needs attention',
            description: 'Please contact EIG Tech Support.',
          }}
          status="error"
        />
      ) : (
        <InputField label="License Number" name="licenseNumber" />
      )}

      {isUpdateError && (
        <ComposedAlert
          alert={{
            title: 'Could not update your license number',
            description: 'Please try again. If the issue persists, contact EIG Tech Support.',
          }}
          status="error"
        />
      )}
    </FormDrawer>
  )
}

const EstimatorLicenseFormSchema = z.object({
  licenseNumber: z.string().min(1, 'License Number field is required'),
})

type EstimatorLicenseForm = z.infer<typeof EstimatorLicenseFormSchema>

const { useForm, InputField } = createForm(EstimatorLicenseFormSchema, 'estimatorLicenseForm')
